import { ClientAccountTypeForSearch, ClientStatusType } from 'src/features/reporting/store/types';
import * as yup from 'yup';

const SchedulerReccurenceValidationSchema = yup
  .object({
    interval: yup.number().nullable().typeError('Interval must be a number.'),
    daysOfWeek: yup.array().of(yup.string().required()).nullable(),
    dayOfMonth: yup
      .number()
      .nullable()
      .min(1, 'Day of month must be between 1 and 31.')
      .max(31, 'Day of month must be between 1 and 31.'),
    weekOfMonth: yup
      .number()
      .nullable()
      .min(1, 'Week of month must be between 1 and 4.')
      .max(4, 'Week of month must be between 1 and 4.'),
    monthOfYear: yup
      .number()
      .nullable()
      .min(1, 'Month of year must be between 1 and 12.')
      .max(12, 'Month of year must be between 1 and 12.'),
  })
  .nullable();

const SchedulerClientSearchValidationSchema = yup
  .object({
    afslIds: yup.array().of(yup.number()).nullable(),
    practiceIds: yup.array().of(yup.number()).nullable(),
    clientIds: yup.array().of(yup.number()).nullable(),
    clientTypes: yup
      .array()
      .of(yup.string().oneOf(ClientAccountTypeForSearch.All))
      .nullable()
      .test('valid-client-types', 'Invalid client type provided.', (val) =>
        Array.isArray(val)
          ? val.every((clientType) => clientType && ClientAccountTypeForSearch.All.includes(clientType))
          : true
      ),
    groupIds: yup.array().of(yup.number()).nullable(),
    statuses: yup
      .array()
      .of(yup.string().oneOf(ClientStatusType.All))
      .nullable()
      .test('valid-statuses', 'Invalid client status provided.', (val) =>
        Array.isArray(val) ? val.every((status) => status && ClientStatusType.All.includes(status)) : true
      ),
    serviceType: yup
      .string()
      .nullable()
      .test(
        'valid-service-type',
        "Service type must be either 'ALL', 'MDA' or 'SOA'.",
        (val) => !val || ['','MDA', 'SOA'].includes(val.toUpperCase())
      ),
  })
  .test('at-least-one-field', 'At least one of the fields must not be empty.', function (value) {
    if (!value) return false;

    const { afslIds, practiceIds, clientIds, clientTypes, groupIds, statuses, serviceType } = value;

    const hasData =
      (afslIds?.length ?? 0) > 0 ||
      (practiceIds?.length ?? 0) > 0 ||
      (clientIds?.length ?? 0) > 0 ||
      (clientTypes?.length ?? 0) > 0 ||
      (groupIds?.length ?? 0) > 0 ||
      (statuses?.length ?? 0) > 0 ||
      (serviceType?.trim() ?? '').length >= 0;

    return hasData;
  });

export const SchedulerValidationSchema = yup.object({
  name: yup.string().required('Name is required.'),
  description: yup.string().nullable(),
  recurrenceType: yup.string().nullable(),
  monthlyRunModeId: yup.number().nullable(),
  startOn: yup.date().required('Start date is required.'),
  endOn: yup
    .date()
    .nullable()
    .test(
      'endOn-after-startOn',
      'End date must be greater than or equal to the start date for recurring events.',
      function (endOn) {
        const { startOn, recurrenceType } = this.parent;
        if (!endOn || !startOn) return true;
        return recurrenceType !== 'Once' && new Date(endOn) >= new Date(startOn);
      }
    ),
  recurrenceRule: SchedulerReccurenceValidationSchema,
  clientSearchFilter: SchedulerClientSearchValidationSchema,
});
